/**
 * Application entry point.
 *
 * Wires the four things that have to exist before any component renders:
 *
 * 1. **The HTTP client's auth binding.** Injected rather than imported, so `http.ts`
 *    stays free of React and can be used by the sync engine outside a component tree.
 * 2. **TanStack Query's defaults.** The retry policy is the interesting part — see below.
 * 3. **The session.** Bootstrapped before the first render so the navigation does not
 *    flash the full menu and then hide half of it.
 * 4. **The feature flags, then whatever they permit** (LV §181, §127-132). The flags
 *    are fetched from the backend as part of the session bootstrap and decide whether
 *    the service worker is registered and the sync loop runs at all — see
 *    `app/feature-flags.ts` for why the client must not assume either.
 */

/**
 * The typeface, self-hosted.
 *
 * `--font-sans` named "Inter" and nothing ever loaded it, so every screen rendered in
 * whatever the operating system offered next — Segoe UI on Windows. A design system
 * that names a face it does not ship has no typography, only a fallback.
 *
 * Self-hosted rather than fetched: nginx sends `font-src 'self'` (deployment/nginx),
 * so a Google Fonts request is blocked outright — and it should be, because this is an
 * offline-capable PWA (LV §127-132) and a screen that loses its typeface in a plant
 * with no signal is a screen that shifts under the reader.
 *
 * `latin-*` subsets only. German needs ä/ö/ü/ß and § and the en dash, all of which are
 * inside Latin-1; the Cyrillic, Greek and Vietnamese cuts fontsource also ships would
 * be dead weight in the service worker's precache.
 */
import "@fontsource/ibm-plex-sans/latin-400.css";
import "@fontsource/ibm-plex-sans/latin-500.css";
import "@fontsource/ibm-plex-sans/latin-600.css";
import "@fontsource/ibm-plex-sans/latin-700.css";
import "@fontsource/ibm-plex-mono/latin-400.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";

import App from "@/app/App";
import { Login } from "@/auth/Login";
import {
  consumeTokenFromFragment,
  refreshAccessToken,
  renewSession,
} from "@/auth/oidc";
import {
  applyDisplayPreferences,
  clearSession,
  getToken,
  setUser,
} from "@/auth/session";
import { identityService } from "@/services";
import { ApiError, configureHttp } from "@/services/http";
import { setChangeDownloader, startAutoSync } from "@/offline/sync";
import {
  featureFlagsAnswered,
  loadFeatureFlags,
  offlineEnabled,
} from "@/app/feature-flags";
import { registerServiceWorker, unregisterServiceWorker } from "@/offline/pwa";

import "@/styles/base.css";

// --- 1. the HTTP client's auth binding
configureHttp({
  getToken,
  // A 401 means the access token is gone or expired. Renew it silently from the refresh
  // cookie (LV §135) — one renewal for however many requests met it — and let the client
  // repeat the refused request; the session ends only if renewal fails. So a technician
  // mid-walkdown is not thrown back to the sign-in screen, or left on a broken page,
  // because the short token lifetime did what it is supposed to do (#411).
  onUnauthorized: renewSession,
});

// --- 2. query defaults
const queryClient = new QueryClient({
  queryCache: new QueryCache(),
  defaultOptions: {
    queries: {
      // 30 seconds: long enough that navigating between a list and a detail does not
      // refetch, short enough that a status changed by a colleague shows up without a
      // manual reload.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      // `networkMode: "always"` is load-bearing, not a tweak.
      //
      // TanStack Query's default ("online") *pauses* a query or mutation while
      // `navigator.onLine` is false: `mutationFn` is never called, the mutation sits in
      // `pending` forever, and no error is ever produced. This application's entire
      // offline strategy is the opposite — LV §127-132 requires the attempt to be made,
      // to fail, and for the failure to put the change in the IndexedDB queue, which is
      // what every `onError` handler here does. Under the default, a technician who
      // finished a walkdown in a dead spot got a spinner that never resolved and a record
      // that was never queued.
      networkMode: "always",
      retry: (failureCount, error) => {
        // Never retry a decided answer. A 403 (site scope), 404, 409 (version
        // conflict) or 422 (business rule) will return the same result however many
        // times it is asked — retrying only delays the German message the user needs
        // to see. A network failure or a 5xx is worth one more attempt.
        if (error instanceof ApiError) {
          if (error.isForbidden || error.isNotFound) return false;
          if (error.isConflict || error.isBusinessRule) return false;
          if (error.isUnauthenticated) return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      // Mutations are never retried automatically. A create that timed out may well
      // have succeeded, and a blind retry would produce a second work order with a
      // second business number. The offline queue handles the genuinely-offline case
      // deliberately (see `offline/sync.ts`).
      retry: false,
      // See the note on queries: without this the offline queue is never reached,
      // because the mutation is paused instead of failing (LV §127-132).
      networkMode: "always",
    },
  },
});

// --- 3. session bootstrap
async function bootstrapSession(): Promise<boolean> {
  // Three ways a session can already exist on load, tried cheapest first:
  //
  // 1. we just came back from the identity provider, and the access token is sitting
  //    in the URL fragment (LV §133);
  // 2. a token is already in memory — a re-render rather than a reload;
  // 3. neither, but the browser still holds the HttpOnly refresh cookie, so the
  //    session survives a page reload without a fresh interactive sign-in (LV §135).
  //
  // Without step 3 a reload would drop the user back to the sign-in screen every time,
  // which is precisely the cost of keeping the token out of web storage — and the
  // reason the refresh endpoint exists.
  consumeTokenFromFragment();
  if (!getToken() && !(await refreshAccessToken())) return false;

  try {
    setUser(await identityService.me());
  } catch {
    clearSession();
    return false;
  }

  // Only now can the offline machinery be started or torn down: the answer to
  // "does this installation run offline" comes from the server, and the call needs
  // the session (LV §134, §181).
  await loadFeatureFlags();
  await applyOfflineFeature();
  return true;
}

// --- 4. offline sync and the PWA (LV §127-132, §181)
/**
 * Start or dismantle offline operation, according to what the server said.
 *
 * The asymmetry is deliberate. Starting is done whenever offline is on; dismantling
 * only when the server actually *answered* that it is off. Unregistering the service
 * worker and dropping its caches is destructive, and a failed flag call is not a
 * decision — see `app/feature-flags.ts`.
 */
async function applyOfflineFeature(): Promise<void> {
  if (offlineEnabled()) {
    await registerServiceWorker();
    // Arch §15's last step, which only this layer can perform: the sync engine knows
    // when the server accepted a queued change, and the query cache is what the screens
    // actually read. Without this a completion reached the server while the order on
    // screen kept the status it had before it was sent (LV §130).
    setChangeDownloader(() => queryClient.invalidateQueries());
    startAutoSync();
    return;
  }
  setChangeDownloader(null);
  if (featureFlagsAnswered()) await unregisterServiceWorker();
}

function Root() {
  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Login onAuthenticated={bootstrapSession}>
            <App />
          </Login>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>
  );
}

const container = document.getElementById("root");
if (!container) throw new Error("#root fehlt im Dokument.");

// Before the first render, so the *sign-in* screen honours them too — it stands in
// front of `App` in the tree above, and this used to live in `App`'s own effect
// (LV §193). See `applyDisplayPreferences`.
applyDisplayPreferences();

createRoot(container).render(<Root />);
